export type ChatMarkdownBlock =
  | { kind: 'paragraph'; body: string }
  | { kind: 'fence'; body: string }
  | { kind: 'quote'; body: string }
  | { kind: 'table'; body: string }
  | { kind: 'header'; level: 1 | 2 | 3 | 4; body: string };

type ParsedBlockSlice = { body: string; nextLineIndex: number };

/**
 * Splits encoded message text into paragraph / fenced code / blockquote blocks.
 */
export function parseDocumentBlocks(text: string): ChatMarkdownBlock[] {
  const normalized = normalizeLineEndings(text);
  const lines = normalized.split('\n');
  const blocks: ChatMarkdownBlock[] = [];
  let lineIndex = 0;
  const hasVisibleContent = textHasNonWhitespaceCharacters(normalized);

  while (lineIndex < lines.length) {
    const line = lines[lineIndex];

    if (isBlankLine(line)) {
      lineIndex += 1;
      continue;
    }

    const trimmedStart = line.trimStart();

    if (isFenceOpening(trimmedStart)) {
      const fence = readFenceBlock(lines, lineIndex);
      blocks.push({ kind: 'fence', body: fence.body });
      lineIndex = fence.nextLineIndex;
      continue;
    }

    if (isHeaderLine(trimmedStart)) {
      const header = readHeaderBlock(trimmedStart);
      blocks.push({ kind: 'header', level: header.level, body: header.body });
      lineIndex += 1;
      continue;
    }

    if (isBlockquoteLine(line)) {
      const quote = readBlockquoteBlock(lines, lineIndex);
      blocks.push({ kind: 'quote', body: quote.body });
      lineIndex = quote.nextLineIndex;
      continue;
    }

    if (isTableOpening(lines, lineIndex)) {
      const table = readTableBlock(lines, lineIndex);
      blocks.push({ kind: 'table', body: table.body });
      lineIndex = table.nextLineIndex;
      continue;
    }

    const paragraph = readParagraphBlock(lines, lineIndex);
    if (paragraph.body.length > 0) {
      blocks.push({ kind: 'paragraph', body: paragraph.body });
    }
    lineIndex = paragraph.nextLineIndex;
  }

  if (blocks.length === 0 && hasVisibleContent) {
    return [{ kind: 'paragraph', body: normalized }];
  }

  return blocks;
}

function normalizeLineEndings(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function textHasNonWhitespaceCharacters(text: string): boolean {
  return text.replace(/\s/g, '').length > 0;
}

function isBlankLine(line: string): boolean {
  return line.trim() === '';
}

function isFenceOpening(trimmedLineStart: string): boolean {
  return trimmedLineStart.startsWith('```');
}

function isHeaderLine(trimmedLineStart: string): boolean {
  return /^#{1,4} /.test(trimmedLineStart);
}

function readHeaderBlock(trimmedLineStart: string): { level: 1 | 2 | 3 | 4; body: string } {
  const match = trimmedLineStart.match(/^(#{1,4}) (.*)/);
  const hashes = match![1];
  const body = match![2] ?? '';
  return { level: hashes.length as 1 | 2 | 3 | 4, body };
}

function isBlockquoteLine(line: string): boolean {
  return /^\s*>/.test(line);
}

function splitTableRow(row: string): string[] {
  const trimmed = row.trim();
  const parts = trimmed.split('|');
  const start = trimmed.startsWith('|') ? 1 : 0;
  const end = trimmed.endsWith('|') ? parts.length - 1 : parts.length;
  return parts.slice(start, end).map(c => c.trim());
}

function isTableLine(line: string): boolean {
  return line.includes('|');
}

function isTableSeparatorLine(line: string): boolean {
  if (!line.includes('|')) {
    return false;
  }
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every(cell => /^:?-+:?$/.test(cell));
}

function isTableOpening(lines: string[], lineIndex: number): boolean {
  if (!isTableLine(lines[lineIndex])) {
    return false;
  }
  const nextLine = lines[lineIndex + 1];
  return !!nextLine && isTableSeparatorLine(nextLine);
}

function readTableBlock(lines: string[], startLineIndex: number): ParsedBlockSlice {
  const bodyLines: string[] = [];
  let index = startLineIndex;
  while (index < lines.length && isTableLine(lines[index])) {
    bodyLines.push(lines[index]);
    index++;
  }
  return { body: bodyLines.join('\n'), nextLineIndex: index };
}

function lineEndsParagraph(line: string, nextLine?: string): boolean {
  if (isBlankLine(line)) {
    return true;
  }
  const trimmedStart = line.trimStart();
  if (isFenceOpening(trimmedStart)) {
    return true;
  }
  if (isHeaderLine(trimmedStart)) {
    return true;
  }
  if (isBlockquoteLine(line)) {
    return true;
  }
  if (nextLine !== undefined && isTableLine(line) && isTableSeparatorLine(nextLine)) {
    return true;
  }
  return false;
}

function blockquoteContentWithoutMarker(line: string): string {
  const trimmedStart = line.trimStart();
  if (!trimmedStart.startsWith('>')) {
    return '';
  }
  let rest = trimmedStart.slice(1);
  if (rest.length > 0 && /\s/.test(rest[0])) {
    rest = rest.slice(1);
  }
  return rest;
}

function readFenceBlock(lines: string[], openingLineIndex: number): ParsedBlockSlice {
  const trimmedStart = lines[openingLineIndex].trimStart();
  const afterOpeningTicks = trimmedStart.slice(3);
  const closingTicksOnSameLine = afterOpeningTicks.indexOf('```');

  if (closingTicksOnSameLine !== -1) {
    return {
      body: afterOpeningTicks.slice(0, closingTicksOnSameLine),
      nextLineIndex: openingLineIndex + 1,
    };
  }

  let index = openingLineIndex + 1;
  const bodyLines: string[] = [];

  // A pure language tag is a single run of word characters (e.g. "python", "javascript").
  // If the opening line has anything beyond that, treat it as the first line of code.
  const isOnlyLanguageTag = /^\w*$/.test(afterOpeningTicks.trim());
  if (!isOnlyLanguageTag && afterOpeningTicks.trim().length > 0) {
    bodyLines.push(afterOpeningTicks);
  }

  while (index < lines.length) {
    const line = lines[index];
    const candidate = line.trimStart();
    if (isFenceOpening(candidate)) {
      return { body: bodyLines.join('\n'), nextLineIndex: index + 1 };
    }
    // Closing ``` appears after code on the same line (e.g. "last line```")
    const closingIdx = line.indexOf('```');
    if (closingIdx !== -1) {
      const lastCodePart = line.slice(0, closingIdx);
      if (lastCodePart.trim().length > 0) {
        bodyLines.push(lastCodePart);
      }
      return { body: bodyLines.join('\n'), nextLineIndex: index + 1 };
    }
    bodyLines.push(line);
    index += 1;
  }

  return { body: bodyLines.join('\n'), nextLineIndex: index };
}

function readBlockquoteBlock(lines: string[], startLineIndex: number): ParsedBlockSlice {
  const bodyLines: string[] = [];
  let index = startLineIndex;

  while (index < lines.length && isBlockquoteLine(lines[index])) {
    bodyLines.push(blockquoteContentWithoutMarker(lines[index]));
    index += 1;
  }

  return { body: bodyLines.join('\n'), nextLineIndex: index };
}

function readParagraphBlock(lines: string[], startLineIndex: number): ParsedBlockSlice {
  const bodyLines: string[] = [];
  let index = startLineIndex;

  while (index < lines.length) {
    const candidate = lines[index];
    if (lineEndsParagraph(candidate, lines[index + 1])) {
      break;
    }
    bodyLines.push(candidate);
    index += 1;
  }

  return { body: bodyLines.join('\n'), nextLineIndex: index };
}
